import { useAtom } from 'jotai/index';
import {
  creatingProjectLoadingAtom, deletingProjectLoadingAtom, downloadingProjectFileLoadingAtom,
  gettingProjectsLoadingAtom,
  projectsAtom, projectsItemsOnPageAtom,
  projectsPagesInfoAtom,
} from '../atoms/projectsAtoms';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import {
  closeModal2Atom,
  closeModalAtom, modal2StateAtom, modalStateAtom, openConfirmModalAtom, openInfoModalAtom, openLoadingModalAtom,
} from '../atoms/modalAtoms';
import useTranslations from './useTranslations';
import NewProjectModal from '../pages/MyProjects/components/NewProjectModal';
import { DICTIONARY } from '../translations/dictionary';
import FilesServices from '../services/FilesServices';
import { isAdminAtom } from '../atoms/accountAtoms';

const useProjects = ({ setSelectedItems } = {}) => {
  const { t } = useTranslations();

  const [isAdmin] = useAtom(isAdminAtom);
  const [projects, setProjects] = useAtom(projectsAtom);
  const [downloadingFileLoading, setDownloadingFileLoading] = useAtom(downloadingProjectFileLoadingAtom);
  const [gettingProjectsLoading, setGettingProjectsLoading] = useAtom(gettingProjectsLoadingAtom);
  const [creatingProjectLoading, setCreatingProjectLoading] = useAtom(creatingProjectLoadingAtom);
  const [deletingProjectLoading, setDeletingProjectLoading] = useAtom(deletingProjectLoadingAtom);
  const [projectsPagesInfo, setProjectsPagesInfo] = useAtom(projectsPagesInfoAtom);
  const [projectsItemsOnPage] = useAtom(projectsItemsOnPageAtom);

  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [, setModal2State] = useAtom(modal2StateAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [, closeModal2] = useAtom(closeModal2Atom);

  const getProjects = async ({ page, shouldAddToExisting }) => {
    const data = await sendRequest({
      request: Api.getProjects,
      payload: { page, items_on_page: projectsItemsOnPage },
      setLoading: setGettingProjectsLoading,
    });

    if (data?.status !== 'ok') return;

    if (page !== 1 && page !== '1' && shouldAddToExisting) setProjects((prev) => [...prev, ...data.data.projects]);
    else setProjects(data.data.projects);

    setProjectsPagesInfo(data.data.pages);
  };

  const createNewProject = (cb) => async (name) => {
    if (creatingProjectLoading) return;

    const data = await sendRequest({
      request: Api.createProject,
      payload: { name, configurations: [] },
      setLoading: setCreatingProjectLoading,
    });

    if (data?.data?.project) {
      setProjects((prev) => [data.data.project, ...prev]);
      setProjectsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records + 1 }));
      cb?.(data?.data?.project);
      closeModal2();
    }
  };

  const deleteProjects = async (listOfIds) => {
    if (deletingProjectLoading) return;

    const data = await sendRequest({
      request: Api.deleteProjects,
      payload: { ids: listOfIds },
      setLoading: setDeletingProjectLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setProjects((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      setProjectsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      closeModal();
    }
  };

  /* Files Start */
  const downloadProjectsArchive = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsArchive,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadPdf(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsPdf = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsPdf,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadPdf(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsDocx = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();

    async function generateAndDownload(projectId) {
      const body = { project_id: projectId };

      const response = await sendRequest({
        request: Api.generateProjectsDocx,
        payload: body,
        setLoading: setDownloadingFileLoading,
        getFullResponse: true,
      });

      const { data, headers } = response || {};

      let isError = false;
      if (data?.status !== 'failed') {
        const downloadRes = FilesServices.downloadDocx(data, headers);
        isError = downloadRes.isError;
      }

      if (!isError) closeModal();
    }

    await Promise.all(ids.map((id) => generateAndDownload(id)));
    closeModal();
  };

  const downloadProjectsFile = async (ids) => {
    if (isAdmin) await downloadProjectsDocx(ids);
    else await downloadProjectsPdf(ids);
  };

  const emailProjectsArchive = async (ids) => {
    if (downloadingFileLoading) return;
    openLoadingModal();
    let isError = false;

    async function generateAndSend(projectId) {
      const body = { project_id: projectId, return_to: 'mail' };

      const data = await sendRequest({
        request: Api.generateProjectsArchive,
        payload: body,
        setLoading: setDownloadingFileLoading,
      });

      if (data?.status === 'failed') {
        isError = true;
      }
    }

    await Promise.all(ids.map((id) => generateAndSend(id)));
    if (!isError) {
      openInfoModal({ text: t(DICTIONARY.emailSuccess) });
    } else {
      closeModal();
    }
  };
  /* Files End */

  const onNewProjectClick = ({ cb } = {}) => {
    setModal2State({
      isOpen: true,
      children: <NewProjectModal onSave={createNewProject(cb)} />,
    });
  };

  const onDeleteProjectsClick = (listOfIds) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDeleteProject) : t(DICTIONARY.sureToDelete, listOfIds.length),
      onButtonClick: () => deleteProjects(listOfIds),
    });
  };

  return {
    projects,
    gettingProjectsLoading,
    projectsPagesInfo,
    getProjects,
    onNewProjectClick,
    onDeleteProjectsClick,
    downloadProjectsArchive,
    downloadProjectsFile,
    emailProjectsArchive,
  };
};

export default useProjects;
